import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/constants/images";
import { waLink } from "@/constants/site";

const DATA = {
  spf: {
    tag: "SPF",
    title: "Surface Protection Film",
    headline: "Invisible Protection. Visible Luxury.",
    intro: "Ultra-clear surface protection films for dining tables, kitchen countertops, furniture and stone — engineered to guard against everyday wear while staying completely invisible.",
    hero: IMG.marble,
    benefits: ["Scratch Protection", "Stain Protection", "Heat Resistance", "UV Protection", "Easy Maintenance", "Crystal Clear Finish"],
    applications: [["Dining Tables", IMG.diningTable], ["Kitchen Countertops", IMG.kitchen], ["Marble & Granite", IMG.marble], ["Office Furniture", IMG.proj2], ["Hotel Furniture", IMG.proj3], ["Glass Surfaces", IMG.glass]],
    cta: "BOOK A SURFACE ASSESSMENT",
    wa: "Hi ERRAH, I'd like to know more about Surface Protection Film (SPF).",
  },
  nano: {
    tag: "NANO",
    title: "Nano Surface Protection",
    headline: "Protection you can't see. Performance you can feel.",
    intro: "Advanced nano coatings that bond to fabrics, upholstery and surfaces — repelling water, resisting stains and preserving the original look and feel of your furniture.",
    hero: IMG.velvetSofa,
    benefits: ["Water Repellent", "Stain Resistant", "Dust Resistant", "UV Protection", "Easy Cleaning", "Surface Preservation"],
    applications: [["Sofas", IMG.sofa], ["Curtains", IMG.curtains], ["Rugs & Carpets", IMG.rug], ["Leather", IMG.furniture], ["Furniture", IMG.velvetSofa], ["Glass & Stone", IMG.glass]],
    cta: "BOOK A CONSULTATION",
    wa: "Hi ERRAH, I'd like to know more about Nano Surface Protection.",
  },
};

export default function SolutionPage({ variant }) {
  const d = DATA[variant];
  const navigate = useNavigate();

  return (
    <div data-testid={`solution-${variant}`}>
      <Seo title={d.title} description={`${d.title} — ${d.headline} ${d.intro}`} />
      <section className="relative min-h-[62vh] flex items-center overflow-hidden">
        <img src={d.hero} alt={d.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/30" />
        <div className="relative max-w-[1500px] mx-auto px-5 lg:px-10">
          <p className="errah-label mb-3">{d.tag}</p>
          <h1 className="serif-head text-5xl lg:text-7xl">{d.title}</h1>
          <p className="mt-4 serif-head text-2xl lg:text-3xl gold-text italic">{d.headline}</p>
          <p className="mt-5 text-gray-300 max-w-xl leading-relaxed">{d.intro}</p>
          <div className="mt-8 flex gap-3">
            <button onClick={() => navigate("/consultation")} className="bg-[#C9A45C] text-black px-7 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors rounded-sm">{d.cta}</button>
            <a href={waLink(d.wa)} target="_blank" rel="noopener noreferrer" className="border border-[#C9A45C]/50 px-7 py-3.5 text-sm font-semibold hover:bg-[#C9A45C] hover:text-black transition-colors rounded-sm">WhatsApp</a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-warmblack border-y border-[#C9A45C]/15">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {d.benefits.map((b) => (
            <div key={b} className="flex items-center gap-2 text-sm text-gray-300"><Check size={16} className="text-[#C9A45C] shrink-0" />{b}</div>
          ))}
        </div>
      </section>

      {/* Applications */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-14">
        <Reveal><h2 className="serif-head text-3xl lg:text-4xl mb-8">Where {d.tag} performs best</h2></Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3.5">
          {d.applications.map(([name, img], i) => (
            <Reveal key={name} delay={(i % 3) * 0.07}>
              <div className="group hover-zoom relative aspect-[16/10] overflow-hidden hair-border">
                <img src={img} alt={name} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                <h3 className="absolute bottom-4 left-4 font-serif text-xl">{name}</h3>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 pb-16">
        <div className="hair-border bg-warmblack p-10 lg:p-14 text-center">
          <h2 className="serif-head text-4xl lg:text-5xl">Ready to protect your surfaces?</h2>
          <p className="mt-3 text-gray-400 max-w-lg mx-auto">Book a professional assessment and our team will recommend the right ERRAH {d.tag} solution.</p>
          <button onClick={() => navigate("/consultation")} className="mt-7 inline-flex items-center gap-2 bg-[#C9A45C] text-black px-8 py-4 text-sm font-semibold hover:bg-[#E4C987] transition-colors rounded-sm">{d.cta} <ArrowRight size={15} /></button>
        </div>
      </section>
    </div>
  );
}
